import { FormikErrors, FormikValues } from 'formik';

export type AddressFieldNames = {
  address1FieldName: string;
  address2FieldName: string;
  cityFieldName: string;
  stateFieldName: string;
  zipFieldName: string;
  latFieldName: string;
  lonFieldName: string;
};

export const setAddressDetails = async (
  placeId: string,
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<FormikValues>>,
  addressFieldNames: AddressFieldNames,
) => {
  if (!placeId) {
    return;
  }

  // Use new Places API
  // Read more: https://developers.google.com/maps/documentation/javascript/places
  const { Place } = (await window.google.maps.importLibrary('places')) as google.maps.PlacesLibrary;
  const place = new Place({
    id: placeId,
  });

  await place.fetchFields({
    fields: ['addressComponents', 'location', 'postalAddress'],
  });

  const components = place.addressComponents;
  const location = place.location;

  if (!components) {
    return;
  }

  const countryCode = components.find((component) => component.types.includes('country'))?.shortText;

  if (countryCode !== 'PR' && location) {
    setFieldValue(addressFieldNames.latFieldName, location.lat());
    setFieldValue(addressFieldNames.lonFieldName, location.lng());
  }

  // Places omits postalAddress for some results
  const { postalAddress } = place;

  if (!postalAddress) {
    return;
  }

  // Extract standardized address data from postalAddress
  const addressLines = postalAddress.addressLines || [];
  const address1 = addressLines[0] || '';
  const address2 = addressLines[1] || '';
  const city = postalAddress.locality || '';
  const stateAbbreviation =
    countryCode === 'PR'
      ? countryCode
      : components.find((component) => component.types.includes('administrative_area_level_1'))?.shortText || '';
  // Extract only the 5-digit ZIP code (remove +4 extension if present)
  const fullZip = postalAddress.postalCode || '';
  const fiveDigitZip = fullZip.split('-')[0];

  // Set address fields from postalAddress
  setFieldValue(addressFieldNames.address1FieldName, address1);
  setFieldValue(addressFieldNames.address2FieldName, address2);
  setFieldValue(addressFieldNames.cityFieldName, city);
  setFieldValue(addressFieldNames.stateFieldName, stateAbbreviation);
  setFieldValue(addressFieldNames.zipFieldName, fiveDigitZip);
};
